import React, {useState} from 'react';
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import useFetchData from "../hooks/use-fetch-data.js";
import {Card} from "../../../../components/card/card.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import {FaBookmark} from "react-icons/fa";
import InputForm from "../../../../components/input/input-form.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import userMessagesService from "../../../../services/web/user-messages-service.js";
import {toast} from "react-toastify";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import {EditorContent, EditorContext, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import RichTextToolbar from "../../../../components/rich-text/rich-text-toolbar.jsx";
import {useLoaderData} from "react-router";
import InputSwitch from "../../../../components/input/input-switch.jsx";

const initialState = {
    roles: [],
    users: [],
    dtValidate: "",
    important: false,
    title: "",
    onlyInGarrison: false
}

const UserMessagesModal = ({open, setOpen, refetch, setIsLoading, isLoading}) => {
    const loaderData = useLoaderData();

    const [state, setState] = useState(initialState);
    const [attaches, setAttaches] = useState([]);

    const {roles, users} = useFetchData();

    const editor = useEditor({
        immediatelyRender: false,
        editorProps: {
            attributes: {
                autocomplete: "off",
                autocorrect: "off",
                autocapitalize: "off",
            },
        },
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Typography,
        ],
    });

    const closeModal = () => {
        setState(initialState);
        setAttaches([]);

        setOpen(false);
    }

    const handleSelect = (value, item) => setState(prev => ({...prev, [item]: value}));

    const handleFilters = (element) => {
        let {id, value, checked, type} = element.target;

        if (type === "checkbox") value = checked;
        setState(prev => ({...prev, [id]: value}));
    }

    const save = () => {
        setIsLoading(true);

        const newMessages = {...state};

        if (newMessages.roles.length) {
            newMessages.users = users.filter(user => newMessages.roles.some(item => user.roles.includes(item.value)));
        }

        if (newMessages.onlyInGarrison) newMessages.users = users.filter(user => user.in_garrison)

        const errorMessages = [
            { condition: !newMessages.users.length, message: "Selecione um destinatário." },
            { condition: !newMessages.title, message: "O campo \"Assunto\" é obrigatório." }
        ];

        for (let { condition, message } of errorMessages) {
            if (condition) {
                projectUtils.swalErrorMessage("", message, "Ok");
                return false;
            }
        }

        setIsLoading(true);

        newMessages.content = editor.getHTML();
        delete newMessages.roles;

        const formData = new FormData();

        newMessages.users = newMessages.users.map(user => user.value);
        newMessages.attaches = attaches.map(attach => {
            const ext = attach.name.split('.').pop();

            formData.append("attaches[]", attach);

            return {
                attachId: attach.name.replace("." + ext, ""),
                extension: ext,
                name: attach.name,
                size: attach.size
            }
        });

        const formJson = JSON.stringify(newMessages);
        const formBlob = new Blob([formJson], {type: 'application/json'});

        formData.append('model', formBlob);

        userMessagesService.save(formData)
            .then(() => {
                editor.commands.setContent("");
                toast.success("Enviado com sucesso");
                closeModal();
                refetch();
            })
            .catch(error => {
                console.error(error);
                projectUtils.swalErrorMessage('', 'Não foi possível enviar a mensagem.', 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const editorClassName = [
        "w-full rounded-md bg-white",
        "focus:outline-none",
        "[&_.ProseMirror]:outline-none",
        //tamanho
        "[&_.ProseMirror]:min-h-63",
        "[&_.ProseMirror]:max-h-92",
        "[&_.ProseMirror]:overflow-auto",
        "[&_.ProseMirror]:py-4 [&_.ProseMirror]:px-6",
        //lista
        "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ul]:my-2",
        "[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ol]:my-2",
        "[&_.ProseMirror_li]:my-1",
        //scroll
        "[&_.ProseMirror::-webkit-scrollbar]:block",
        "[&_.ProseMirror::-webkit-scrollbar]:w-2",
        "[&_.ProseMirror::-webkit-scrollbar-track]:bg-transparent",
        "[&_.ProseMirror::-webkit-scrollbar-thumb]:rounded",
        "[&_.ProseMirror::-webkit-scrollbar-thumb]:bg-zinc-200",
        "[&_.ProseMirror]:[scrollbar-width:thin]",
        "[&_.ProseMirror]:[scrollbar-color:rgb(228_228_231)_transparent]",
        //text
        "[&_.ProseMirror]:leading-9",
        "[&_.ProseMirror]:text-zinc-700",
        "[&_.ProseMirror]:!text-xs",
        "[&_.ProseMirror_p]:!text-xs",
        //link
        "[&_.ProseMirror_a:hover]:cursor-pointer",
        "[&_.ProseMirror_a:hover]:text-blue-500",
    ].join(" ");

    return (
        <Modal open={open} setOpen={closeModal} title={"Nova mensagem"} overflow={true} className={"w-11/12"}>
            <Modal.Body className={"space-y-3 overflow-auto h-[calc(100vh-200px)]"}>
                <Card>
                    <Card.Header>
                        <Card.Title>Destinatário</Card.Title>
                    </Card.Header>

                    <Card.Body>
                        <SentryRow className={"items-center"}>
                            <SelectComponent
                                classNameWrapper={"col-span-6"}
                                value={state.roles}
                                options={roles ?? []}
                                label={"Funções *"}
                                isDisabled={!!state.users.length || state.onlyInGarrison}
                                onChange={(value) => handleSelect(value, "roles")}
                                isMulti
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-6"}
                                value={state.users}
                                options={users ?? []}
                                label={"Agentes *"}
                                isDisabled={!!state.roles.length || state.onlyInGarrison}
                                onChange={(value) => handleSelect(value, "users")}
                                isMulti
                            />

                            {
                                loaderData?.hasDispatch &&
                                <InputSwitch
                                    id={"onlyInGarrison"}
                                    className={"col-span-4"}
                                    label={"Apenas ativos no despacho"}
                                    checked={state.onlyInGarrison}
                                    disabled={!!state.users.length || !!state.roles.length}
                                    onChange={(checked) => setState(prev => ({...prev, onlyInGarrison: checked}))}
                                />
                            }
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Header>
                        <Card.Title>Mensagem</Card.Title>
                    </Card.Header>
                    <Card.Body className={"space-y-4"}>
                        <div className="flex items-center gap-4">
                            <InputDatetime
                                id={"dtEnd"}
                                label={"Data de expiração"}
                                selected={state.dtValidate}
                                dateFormat="LL"
                                classNameWrapper={"w-1/4"}
                                onChange={(date) => setState(prev => ({...prev, dtValidate: date}))}
                            />

                            <InputCheckbox
                                id={"important"}
                                label={<span className={"flex gap-1 items-center"}>Importante <FaBookmark color={"#ffca28"}/></span>}
                                checked={state.important}
                                onChange={handleFilters}
                            />
                        </div>

                        <InputForm
                            id={"title"}
                            label={"Assunto *"}
                            value={state.title}
                            onChange={handleFilters}
                        />

                        <p className="p-0 !m-0">Conteúdo:</p>
                        <EditorContext.Provider value={{ editor }}>
                            <RichTextToolbar wrapperClass={"bg-white !border-zinc-200 !rounded-t-md !rounded-b-none !mb-0"} />
                            <div className={"rounded-b-md border-t-0 border border-zinc-200 bg-white"}>
                                <EditorContent editor={editor} className={editorClassName}/>
                            </div>
                        </EditorContext.Provider>

                    </Card.Body>
                </Card>

                <Dropzone title={"Anexos"} attaches={attaches} setAttaches={setAttaches}/>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={save} loading={isLoading} loadingText={"Enviando..."} successText="Enviado com sucesso" actionFeedbackType="save">
                    Enviar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserMessagesModal;